import React, { useState, useEffect, useCallback } from 'react';
import { Search, ChevronLeft, ChevronRight, CheckCircle, Send, Clock, FileText, X, LayoutDashboard, TrendingUp, Package, DollarSign, Truck, Users, Calendar, Edit2, ArrowUp, ArrowDown, ArrowUpDown, RotateCcw } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { dashboardAPI } from '../api';
import Toast from '../components/Toast';

const COLORS = ['#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6'];

function formatCurrency(val) {
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val || 0);
}

function formatDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatNumber(val) {
  return new Intl.NumberFormat('en-PH').format(val || 0);
}

// The local calendar date. toISOString() returns the UTC date, which in the
// Philippines is still "yesterday" for any local time before 08:00, so a
// bulk receive would stamp the wrong date on every row it touches.
function todayLocal() {
  const now = new Date();
  const offsetMs = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - offsetMs).toISOString().slice(0, 10);
}

function calcAging(deliveryDate) {
  if (!deliveryDate) return '-';
  const now = new Date();
  const del = new Date(deliveryDate);
  const days = Math.floor((now - del) / 86400000);
  return days;
}

function agingColor(days) {
  if (days <= 30) return '#10b981';
  if (days <= 60) return '#f59e0b';
  if (days <= 90) return '#f97316';
  return '#ef4444';
}

function calculateDueDate(deliveryDate, paymentTerms) {
  if (!deliveryDate || !paymentTerms) return null;
  
  const terms = paymentTerms.toString().toUpperCase().trim();
  
  // COD = Cash on Delivery, due date is the same as delivery date
  if (terms === 'COD') {
    return new Date(deliveryDate);
  }
  
  // Extract number from payment terms (e.g., "N30" -> 30, "Net 15" -> 15)
  const match = paymentTerms.toString().match(/(\d+)/);
  const days = match ? parseInt(match[1]) : 0;
  
  if (days === 0) return null;
  
  const date = new Date(deliveryDate);
  date.setDate(date.getDate() + days);
  return date;
}

function isOverdue(dueDate) {
  if (!dueDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return dueDate < today;
}

export default function ARReceived() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [deliveries, setDeliveries] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 15, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState(null);
  const [receiveModal, setReceiveModal] = useState(null);
  const [receiveForm, setReceiveForm] = useState({ date_received: '', remarks: '' });
  const [editDateModal, setEditDateModal] = useState(null);
  const [editDateForm, setEditDateForm] = useState({ date_received: '', remarks: '' });
  const [dashboardStats, setDashboardStats] = useState(null);
  const [dailyData, setDailyData] = useState([]);
  const [topDrivers, setTopDrivers] = useState([]);
  const [topCustomers, setTopCustomers] = useState([]);
  const [days, setDays] = useState(30);
  const [dateDeliveredFrom, setDateDeliveredFrom] = useState('');
  const [dateDeliveredTo, setDateDeliveredTo] = useState('');
  // The Pending AR tab keeps its own delivered-date range so the two tabs
  // do not share a filter by accident.
  const [pendingDateFrom, setPendingDateFrom] = useState('');
  const [pendingDateTo, setPendingDateTo] = useState('');
  const [picker, setPicker] = useState('');
  const [pickers, setPickers] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('DESC');

  // Bulk selection for the Pending AR tab. Each selected row is kept as a small
  // snapshot so the count and total amount stay correct even when the user
  // moves to another page before applying the bulk receive.
  const [selected, setSelected] = useState({});
  const [bulkModal, setBulkModal] = useState(false);
  const [bulkForm, setBulkForm] = useState({ date_received: '', remarks: '', color_ar: '' });
  const [bulkSaving, setBulkSaving] = useState(false);

  async function load(page = 1) {
    setLoading(true);
    try {
      if (activeTab === 'dashboard') {
        const [statsRes, dailyRes, driversRes, customersRes] = await Promise.all([
          dashboardAPI.getStats(),
          dashboardAPI.getDailySummary(days),
          dashboardAPI.getTopDrivers(days),
          dashboardAPI.getTopCustomers(days),
        ]);
        setDashboardStats(statsRes.data);
        setDailyData(dailyRes.data);
        setTopDrivers(driversRes.data);
        setTopCustomers(customersRes.data);
        setDeliveries([]);
        setPagination({ page: 1, limit: 15, total: 0, totalPages: 0 });
      } else {
        const sortQuery = sortBy ? `&sortBy=${encodeURIComponent(sortBy)}&order=${sortOrder}` : '';
        let url;
        if (activeTab === 'pending') {
          url = `/api/deliveries/ar-pending?page=${page}&limit=15&search=${encodeURIComponent(search)}&date_delivered_from=${encodeURIComponent(pendingDateFrom)}&date_delivered_to=${encodeURIComponent(pendingDateTo)}${sortQuery}`;
        } else if (activeTab === 'received') {
          url = `/api/deliveries/ar-received?page=${page}&limit=15&search=${encodeURIComponent(search)}&date_delivered_from=${encodeURIComponent(dateDeliveredFrom)}&date_delivered_to=${encodeURIComponent(dateDeliveredTo)}&picker=${encodeURIComponent(picker)}${sortQuery}`;
        } else {
          // All Deliveries - show ALL statuses
          url = `/api/deliveries?page=${page}&limit=50&search=${encodeURIComponent(search)}${statusFilter ? `&status=${encodeURIComponent(statusFilter)}` : ''}${sortQuery}`;
        }
        const res = await fetch(url, { credentials: 'include' });
        const data = await res.json();

        // The last page can shrink (a bulk receive may empty it), so fall back
        // to the last page that still holds rows instead of showing an empty
        // table with "Page 3 of 2".
        const pageInfo = data.pagination;
        if (pageInfo && pageInfo.totalPages > 0 && pageInfo.page > pageInfo.totalPages) {
          return load(pageInfo.totalPages);
        }

        setDeliveries(data.data || []);
        setPagination(data.pagination || { page: 1, limit: 15, total: 0, totalPages: 0 });
      }
    } catch (err) {
      console.error('Load error:', err);
    }
    setLoading(false);
  }

  useEffect(() => { load(); }, [activeTab, search, days, dateDeliveredFrom, dateDeliveredTo, pendingDateFrom, pendingDateTo, picker, statusFilter, sortBy, sortOrder]);

  useEffect(() => {
    fetch('/api/deliveries/meta/pickers', { credentials: 'include' })
      .then(res => res.json())
      .then(data => setPickers(data))
      .catch(err => console.error('Failed to load pickers:', err));
  }, []);

  // Clicking the header of the active column flips the direction, clicking a
  // new column sorts by it. Dates and amounts read best from the oldest or
  // the smallest value, so they start ascending.
  const SORT_DEFAULT_ORDER = {
    date_delivered: 'ASC',
    due_date: 'ASC',
    aging_days: 'ASC',
    invoice_number: 'ASC',
    customer_code: 'ASC',
    customer_name: 'ASC',
    payment_terms: 'ASC',
    amount: 'ASC'
  };

  function handleSort(column) {
    if (sortBy === column) {
      setSortOrder(prev => prev === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(column);
      setSortOrder(SORT_DEFAULT_ORDER[column] || 'ASC');
    }
  }

  function SortIcon({ column }) {
    if (sortBy !== column) return <ArrowUpDown size={12} style={{ opacity: 0.3, marginLeft: 4 }} />;
    return sortOrder === 'ASC'
      ? <ArrowUp size={12} style={{ marginLeft: 4, color: '#3b82f6' }} />
      : <ArrowDown size={12} style={{ marginLeft: 4, color: '#3b82f6' }} />;
  }

  function SortableTh({ column, children }) {
    const isActive = sortBy === column;
    return (
      <th
        onClick={() => handleSort(column)}
        title={`Sort by ${children}`}
        style={{
          cursor: 'pointer',
          userSelect: 'none',
          whiteSpace: 'nowrap',
          background: isActive ? '#f0f9ff' : undefined,
          transition: 'background 0.15s'
        }}
        onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#f8fafc'; }}
        onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = ''; }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          {children}
          <SortIcon column={column} />
        </span>
      </th>
    );
  }

  function openReceiveModal(row) {
    setReceiveModal(row);
    setReceiveForm({ date_received: todayLocal(), remarks: '', color_ar: '' });
  }

  // ---- Bulk receive helpers (Pending AR tab) ----

  // Only deliveries that are already DELIVERED and still waiting for AR can be
  // received, so those are the only rows that get a checkbox.
  function isSelectable(d) {
    return d.delivery_status === 'DELIVERED' && !d.date_received_by_ar;
  }

  function snapshotRow(d) {
    return {
      id: d.id,
      invoice_number: d.invoice_number,
      customer_name: d.customer_name,
      amount: d.amount
    };
  }

  function toggleRowSelection(d) {
    if (!isSelectable(d)) return;
    setSelected(prev => {
      const next = { ...prev };
      if (next[d.id]) {
        delete next[d.id];
      } else {
        next[d.id] = snapshotRow(d);
      }
      return next;
    });
  }

  // Selects or clears the rows on the page that is currently on screen.
  // Selections made on other pages are left untouched.
  function toggleSelectAll() {
    const selectable = deliveries.filter(isSelectable);
    const allSelected = selectable.length > 0 && selectable.every(d => selected[d.id]);
    setSelected(prev => {
      const next = { ...prev };
      selectable.forEach(d => {
        if (allSelected) {
          delete next[d.id];
        } else {
          next[d.id] = snapshotRow(d);
        }
      });
      return next;
    });
  }

  function clearSelection() {
    setSelected({});
  }

  // Drops one row from the selection, used when that row leaves the pending
  // list on its own (for example after it was received one by one).
  function removeFromSelection(id) {
    setSelected(prev => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }

  function openBulkReceiveModal() {
    setBulkForm({ date_received: todayLocal(), remarks: '', color_ar: '' });
    setBulkModal(true);
  }

  async function handleBulkReceive(e) {
    e.preventDefault();
    setBulkSaving(true);
    try {
      const res = await fetch('/api/deliveries/ar-receive-bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ids: selectedIds, ...bulkForm })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Bulk receive failed');
      setToast({
        message: `${data.count} deliver${data.count === 1 ? 'y' : 'ies'} received by AR — ${formatCurrency(data.total_amount)}${data.skipped ? ` (${data.skipped} skipped, no longer pending)` : ''}`,
        type: 'success',
        duration: 5000
      });
      setBulkModal(false);
      setSelected({});
      load(pagination.page);
    } catch (err) {
      setToast({ message: err.message || 'Failed to bulk receive deliveries', type: 'error', duration: 4000 });
    } finally {
      setBulkSaving(false);
    }
  }

  async function handleReceive(e) {
    e.preventDefault();
    try {
      await fetch(`/api/deliveries/${receiveModal.id}/ar-receive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(receiveForm),
      });
      setToast({ message: `Delivery ${receiveModal.invoice_number} received by AR`, type: 'success', duration: 3000 });
      setReceiveModal(null);
      // This row just left the pending list, so drop it from the selection
      removeFromSelection(receiveModal.id);
      load(pagination.page);
    } catch (err) {
      setToast({ message: 'Failed to receive delivery', type: 'error', duration: 3000 });
    }
  }

  async function handleRelease(row) {
    if (!window.confirm(`Release ${row.invoice_number} to AR?\n\nPosting Date will be set to the PSI document date.`)) return;
    try {
      const res = await fetch(`/api/deliveries/${row.id}/ar-release`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (res.ok) {
        setToast({ message: `Delivery ${row.invoice_number} released. Posting Date: ${data.ar_released_date}`, type: 'success', duration: 4000 });
        load(pagination.page);
      } else {
        setToast({ message: data.error || 'Failed to release', type: 'error', duration: 3000 });
      }
    } catch (err) {
      setToast({ message: 'Failed to release delivery', type: 'error', duration: 3000 });
    }
  }

  function openEditDateModal(row) {
    setEditDateModal(row);
    setEditDateForm({ 
      date_received: row.date_received_by_ar ? new Date(row.date_received_by_ar).toISOString().slice(0, 10) : todayLocal(),
      remarks: row.ar_remarks || ''
    });
  }

  async function handleEditDate(e) {
    e.preventDefault();
    try {
      await fetch(`/api/deliveries/${editDateModal.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          date_received_by_ar: editDateForm.date_received,
          ar_remarks: editDateForm.remarks
        }),
      });
      setToast({ message: `Updated date received for ${editDateModal.invoice_number}`, type: 'success', duration: 3000 });
      setEditDateModal(null);
      load(pagination.page);
    } catch (err) {
      setToast({ message: 'Failed to update date', type: 'error', duration: 3000 });
    }
  }

  // ---- Derived values for the bulk selection ----
  const selectedIds = Object.keys(selected).map(id => Number(id));
  const selectedCount = selectedIds.length;
  const selectedAmount = selectedIds.reduce((sum, id) => sum + Number(selected[id]?.amount || 0), 0);
  const pageSelectable = deliveries.filter(isSelectable);
  const pageSelectedCount = pageSelectable.filter(d => selected[d.id]).length;
  const allPageSelected = pageSelectable.length > 0 && pageSelectedCount === pageSelectable.length;
  const somePageSelected = pageSelectedCount > 0 && !allPageSelected;

  return (
    <div>
      {toast && <Toast message={toast.message} type={toast.type} duration={toast.duration} onClose={() => setToast(null)} />}

      {/* Receive Modal */}
      {receiveModal && (
        <div className="modal-overlay" onClick={() => setReceiveModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <h3>Receive by AR — {receiveModal.invoice_number}</h3>
              <button className="modal-close" onClick={() => setReceiveModal(null)}><X size={20} /></button>
            </div>
            <form onSubmit={handleReceive}>
              <div className="modal-body">
                <div style={{ marginBottom: 16, padding: 12, background: '#f0f9ff', borderRadius: 8, fontSize: 13 }}>
                  <div><strong>Customer:</strong> {receiveModal.customer_name}</div>
                  <div><strong>Amount:</strong> {formatCurrency(receiveModal.amount)}</div>
                  <div><strong>Delivery Date:</strong> {formatDate(receiveModal.date)}</div>
                  <div><strong>Aging:</strong> <span style={{ color: agingColor(calcAging(receiveModal.date)), fontWeight: 700 }}>{calcAging(receiveModal.date)} days</span></div>
                </div>
                <div className="form-group">
                  <label>Date Received by AR</label>
                  <input type="date" value={receiveForm.date_received} onChange={e => setReceiveForm(prev => ({ ...prev, date_received: e.target.value }))} required />
                </div>
                <div className="form-group" style={{ marginTop: 12 }}>
                  <label>Color AR</label>
                  <input value={receiveForm.color_ar} onChange={e => setReceiveForm(prev => ({ ...prev, color_ar: e.target.value }))} placeholder="e.g., Green, Yellow, Red" />
                </div>
                <div className="form-group" style={{ marginTop: 12 }}>
                  <label>AR Remarks</label>
                  <textarea value={receiveForm.remarks} onChange={e => setReceiveForm(prev => ({ ...prev, remarks: e.target.value }))} rows={3} placeholder="e.g., Complete documents received, COD collected, etc." />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setReceiveModal(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary" style={{ background: '#10b981' }}>
                  <CheckCircle size={16} /> Receive
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Date Received Modal */}
      {editDateModal && (
        <div className="modal-overlay" onClick={() => setEditDateModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <h3>Edit Date Received — {editDateModal.invoice_number}</h3>
              <button className="modal-close" onClick={() => setEditDateModal(null)}><X size={20} /></button>
            </div>
            <form onSubmit={handleEditDate}>
              <div className="modal-body">
                <div style={{ marginBottom: 16, padding: 12, background: '#f0f9ff', borderRadius: 8, fontSize: 13 }}>
                  <div><strong>Customer:</strong> {editDateModal.customer_name}</div>
                  <div><strong>Amount:</strong> {formatCurrency(editDateModal.amount)}</div>
                  <div><strong>Current Date Received:</strong> {formatDate(editDateModal.date_received_by_ar)}</div>
                </div>
                <div className="form-group">
                  <label>Date Received by AR</label>
                  <input type="date" value={editDateForm.date_received} onChange={e => setEditDateForm(prev => ({ ...prev, date_received: e.target.value }))} required />
                </div>
                <div className="form-group" style={{ marginTop: 12 }}>
                  <label>AR Remarks</label>
                  <textarea value={editDateForm.remarks} onChange={e => setEditDateForm(prev => ({ ...prev, remarks: e.target.value }))} rows={3} placeholder="e.g., Complete documents received, COD collected, etc." />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setEditDateModal(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary" style={{ background: '#3b82f6' }}>
                  <Edit2 size={16} /> Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Receive Modal */}
      {bulkModal && (
        <div className="modal-overlay" onClick={() => !bulkSaving && setBulkModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <h3>Bulk Receive by AR</h3>
              <button className="modal-close" onClick={() => setBulkModal(false)} disabled={bulkSaving}><X size={20} /></button>
            </div>
            <form onSubmit={handleBulkReceive}>
              <div className="modal-body">
                <div style={{ marginBottom: 16, padding: 12, background: '#f0fdf4', borderRadius: 8, fontSize: 13, borderLeft: '4px solid #10b981' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ color: '#475569' }}>Deliveries to receive</span>
                    <strong style={{ color: '#065f46' }}>{selectedCount}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ color: '#475569' }}>Total amount (selected)</span>
                    <strong style={{ color: '#065f46' }}>{formatCurrency(selectedAmount)}</strong>
                  </div>
                  <div style={{ color: '#64748b', fontSize: 12, marginTop: 8, lineHeight: 1.6, maxHeight: 110, overflowY: 'auto', background: '#ffffff', border: '1px solid #d1fae5', borderRadius: 6, padding: '6px 8px' }}>
                    {selectedIds.map(id => selected[id]?.invoice_number).filter(Boolean).join(', ') || 'No invoice numbers available'}
                  </div>
                </div>

                <div className="form-group">
                  <label>Date Received by AR</label>
                  <input type="date" value={bulkForm.date_received} onChange={e => setBulkForm(prev => ({ ...prev, date_received: e.target.value }))} required />
                </div>
                <div className="form-group" style={{ marginTop: 12 }}>
                  <label>Color AR</label>
                  <input value={bulkForm.color_ar} onChange={e => setBulkForm(prev => ({ ...prev, color_ar: e.target.value }))} placeholder="e.g., Green, Yellow, Red" />
                </div>
                <div className="form-group" style={{ marginTop: 12 }}>
                  <label>AR Remarks</label>
                  <textarea value={bulkForm.remarks} onChange={e => setBulkForm(prev => ({ ...prev, remarks: e.target.value }))} rows={3} placeholder="e.g., Complete documents received, COD collected, etc." />
                </div>

                <div style={{ marginTop: 14, padding: 10, background: '#fffbeb', borderRadius: 8, fontSize: 12, color: '#92400e' }}>
                  The same date, color and remarks will be applied to all {selectedCount} selected deliver{selectedCount === 1 ? 'y' : 'ies'}.
                  Deliveries that are no longer pending AR will be skipped.
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setBulkModal(false)} disabled={bulkSaving}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={bulkSaving} style={{ background: '#10b981', display: 'flex', alignItems: 'center', gap: 6 }}>
                  {bulkSaving ? (
                    <span style={{
                      width: 14, height: 14,
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTopColor: 'white',
                      borderRadius: '50%',
                      display: 'inline-block',
                      animation: 'spin 0.6s linear infinite'
                    }} />
                  ) : <CheckCircle size={16} />}
                  {bulkSaving ? 'Receiving...' : `Receive ${selectedCount} Deliver${selectedCount === 1 ? 'y' : 'ies'}`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="table-container">
        <div className="table-header table-title-row">
          <h3>AR Processing</h3>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '2px solid #e2e8f0', padding: '0 16px' }}>
          <button
            onClick={() => { setActiveTab('dashboard'); setSearch(''); setStatusFilter(''); setSortBy(''); setSortOrder('DESC'); setSelected({}); }}
            style={{
              padding: '10px 20px', border: 'none', borderBottom: activeTab === 'dashboard' ? '2px solid #3b82f6' : '2px solid transparent',
              background: 'none', cursor: 'pointer', fontSize: 14, fontWeight: activeTab === 'dashboard' ? 600 : 400,
              color: activeTab === 'dashboard' ? '#3b82f6' : '#64748b', marginBottom: -2, display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'inherit'
            }}
          >
            <LayoutDashboard size={16} /> Dashboard
          </button>
          <button
            onClick={() => { setActiveTab('pending'); setSearch(''); setStatusFilter(''); setSortBy(''); setSortOrder('DESC'); setSelected({}); }}
            style={{
              padding: '10px 20px', border: 'none', borderBottom: activeTab === 'pending' ? '2px solid #3b82f6' : '2px solid transparent',
              background: 'none', cursor: 'pointer', fontSize: 14, fontWeight: activeTab === 'pending' ? 600 : 400,
              color: activeTab === 'pending' ? '#3b82f6' : '#64748b', marginBottom: -2, display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'inherit'
            }}
          >
            <Clock size={16} /> Pending AR ({dashboardStats?.pendingAR ?? pagination.total})
          </button>
          <button
            onClick={() => { setActiveTab('received'); setSearch(''); setStatusFilter(''); setSortBy(''); setSortOrder('DESC'); setSelected({}); }}
            style={{
              padding: '10px 20px', border: 'none', borderBottom: activeTab === 'received' ? '2px solid #3b82f6' : '2px solid transparent',
              background: 'none', cursor: 'pointer', fontSize: 14, fontWeight: activeTab === 'received' ? 600 : 400,
              color: activeTab === 'received' ? '#3b82f6' : '#64748b', marginBottom: -2, display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'inherit'
            }}
          >
            <CheckCircle size={16} /> AR Received
          </button>
          <button
            onClick={() => { setActiveTab('all'); setSearch(''); setStatusFilter(''); setSortBy(''); setSortOrder('DESC'); setSelected({}); }}
            style={{
              padding: '10px 20px', border: 'none', borderBottom: activeTab === 'all' ? '2px solid #3b82f6' : '2px solid transparent',
              background: 'none', cursor: 'pointer', fontSize: 14, fontWeight: activeTab === 'all' ? 600 : 400,
              color: activeTab === 'all' ? '#3b82f6' : '#64748b', marginBottom: -2, display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'inherit'
            }}
          >
            <FileText size={16} /> All Deliveries
          </button>
        </div>

        {activeTab === 'dashboard' ? (
          <div>
            {loading ? (
              <div className="loading"><div className="spinner"></div> Loading dashboard...</div>
            ) : dashboardStats ? (
              <>
                {/* Summary Cards - same style as main Dashboard */}
                <div className="stats-grid">
                  <div className="stat-card">
                    <div className="stat-header">
                      <span className="stat-label">Total Deliveries</span>
                      <div className="stat-icon icon-blue"><Truck size={20} /></div>
                    </div>
                    <div className="stat-value">{formatNumber(dashboardStats.totalDeliveries)}</div>
                    <div className="stat-sub">{formatCurrency(dashboardStats.totalAmount)}</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-header">
                      <span className="stat-label">Pending AR</span>
                      <div className="stat-icon icon-amber"><Clock size={20} /></div>
                    </div>
                    <div className="stat-value">{formatNumber(dashboardStats.pendingAR)}</div>
                    <div className="stat-sub">{formatCurrency(dashboardStats.pendingARAmount)}</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-header">
                      <span className="stat-label">AR Received</span>
                      <div className="stat-icon icon-green"><CheckCircle size={20} /></div>
                    </div>
                    <div className="stat-value">{formatNumber(dashboardStats.receivedAR)}</div>
                    <div className="stat-sub">{formatCurrency(dashboardStats.receivedARAmount)}</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-header">
                      <span className="stat-label">AR Released</span>
                      <div className="stat-icon icon-purple"><Send size={20} /></div>
                    </div>
                    <div className="stat-value">{formatNumber(dashboardStats.releasedAR)}</div>
                    <div className="stat-sub">{formatCurrency(dashboardStats.releasedARAmount)}</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-header">
                      <span className="stat-label">Today's Dispatches</span>
                      <div className="stat-icon icon-amber"><Calendar size={20} /></div>
                    </div>
                    <div className="stat-value">{formatNumber(dashboardStats.todayDispatches)}</div>
                    <div className="stat-sub">{formatCurrency(dashboardStats.todayAmount)}</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-header">
                      <span className="stat-label">This Month</span>
                      <div className="stat-icon icon-purple"><TrendingUp size={20} /></div>
                    </div>
                    <div className="stat-value">{formatNumber(dashboardStats.monthDispatches)}</div>
                    <div className="stat-sub">{formatCurrency(dashboardStats.monthAmount)}</div>
                  </div>
                </div>

                {/* Charts Row 1 - Dispatch Trend + Status Distribution */}
                <div className="charts-grid">
                  <div className="chart-card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                      <h3 style={{ margin: 0 }}>Dispatch Trend</h3>
                      <select className="filter-select" value={days} onChange={e => setDays(Number(e.target.value))}>
                        <option value={7}>Last 7 days</option>
                        <option value={30}>Last 30 days</option>
                        <option value={90}>Last 90 days</option>
                      </select>
                    </div>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={dailyData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="dispatch_date" tick={{ fontSize: 11 }} tickFormatter={v => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip labelFormatter={v => new Date(v).toLocaleDateString()} />
                        <Legend />
                        <Line type="monotone" dataKey="total_dispatches" stroke="#3b82f6" strokeWidth={2} name="Total" dot={false} />
                        <Line type="monotone" dataKey="delivered" stroke="#10b981" strokeWidth={2} name="Delivered" dot={false} />
                        <Line type="monotone" dataKey="pending" stroke="#f59e0b" strokeWidth={2} name="Pending" dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="chart-card">
                    <h3>Status Distribution</h3>
                    {dashboardStats.statusBreakdown && dashboardStats.statusBreakdown.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie data={dashboardStats.statusBreakdown.map(s => ({ name: s.delivery_status || 'UNKNOWN', value: s.count }))} cx="50%" cy="50%" innerRadius={60} outerRadius={100} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                            {dashboardStats.statusBreakdown.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : <p style={{ color: '#64748b', textAlign: 'center', paddingTop: 60 }}>No data</p>}
                  </div>
                </div>

                {/* Charts Row 2 - Top Drivers + Top Customers */}
                <div className="charts-grid" style={{ marginTop: 0 }}>
                  <div className="chart-card">
                    <h3>Top Drivers ({days} days)</h3>
                    {topDrivers.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={topDrivers} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis type="number" tick={{ fontSize: 11 }} />
                          <YAxis type="category" dataKey="driver" tick={{ fontSize: 11 }} width={100} />
                          <Tooltip />
                          <Bar dataKey="trip_count" fill="#3b82f6" name="Trips" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : <p style={{ color: '#64748b', textAlign: 'center', paddingTop: 60 }}>No driver data</p>}
                  </div>

                  <div className="chart-card">
                    <h3>Top Customers ({days} days)</h3>
                    <div className="table-scroll" style={{ maxHeight: 300, overflowY: 'auto' }}>
                      <table>
                        <thead>
                          <tr><th>Customer</th><th style={{ textAlign: 'right' }}>Amount</th></tr>
                        </thead>
                        <tbody>
                          {topCustomers.map((c, i) => (
                            <tr key={i}>
                              <td style={{ fontSize: 13 }}>{c.customer_name}</td>
                              <td style={{ textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(c.total_amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {topCustomers.length === 0 && <p style={{ color: '#64748b', textAlign: 'center', padding: 40 }}>No customer data</p>}
                    </div>
                  </div>
                </div>

                {/* AR-Specific: Pending AR Aging */}
                <div className="charts-grid" style={{ marginTop: 0 }}>
                  <div className="chart-card">
                    <h3>Pending AR Aging</h3>
                    {dashboardStats.agingSummary ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#f0fdf4', borderRadius: 8, borderLeft: '4px solid #10b981' }}>
                          <span style={{ fontSize: 13, color: '#475569' }}>0 - 30 days</span>
                          <span style={{ fontSize: 18, fontWeight: 700, color: '#10b981' }}>{dashboardStats.agingSummary.within_30 || 0}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#fffbeb', borderRadius: 8, borderLeft: '4px solid #f59e0b' }}>
                          <span style={{ fontSize: 13, color: '#475569' }}>31 - 60 days</span>
                          <span style={{ fontSize: 18, fontWeight: 700, color: '#f59e0b' }}>{dashboardStats.agingSummary.days_31_60 || 0}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#fff7ed', borderRadius: 8, borderLeft: '4px solid #f97316' }}>
                          <span style={{ fontSize: 13, color: '#475569' }}>61 - 90 days</span>
                          <span style={{ fontSize: 18, fontWeight: 700, color: '#f97316' }}>{dashboardStats.agingSummary.days_61_90 || 0}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#fef2f2', borderRadius: 8, borderLeft: '4px solid #ef4444' }}>
                          <span style={{ fontSize: 13, color: '#475569' }}>Over 90 days</span>
                          <span style={{ fontSize: 18, fontWeight: 700, color: '#ef4444' }}>{dashboardStats.agingSummary.over_90 || 0}</span>
                        </div>
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: 20, color: '#94a3b8' }}>No pending AR data</div>
                    )}
                  </div>

                  <div className="chart-card">
                    <h3>AR Summary</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <div style={{ padding: '14px 16px', background: '#f0fdf4', borderRadius: 8, borderLeft: '4px solid #10b981' }}>
                        <div style={{ fontSize: 13, color: '#475569' }}>AR Received</div>
                        <div style={{ fontSize: 20, fontWeight: 700, color: '#10b981' }}>{formatNumber(dashboardStats.receivedAR)}</div>
                        <div style={{ fontSize: 12, color: '#64748b' }}>{formatCurrency(dashboardStats.receivedARAmount)}</div>
                      </div>
                      <div style={{ padding: '14px 16px', background: '#fef3c7', borderRadius: 8, borderLeft: '4px solid #f59e0b' }}>
                        <div style={{ fontSize: 13, color: '#475569' }}>Pending AR</div>
                        <div style={{ fontSize: 20, fontWeight: 700, color: '#f59e0b' }}>{formatNumber(dashboardStats.pendingAR)}</div>
                        <div style={{ fontSize: 12, color: '#64748b' }}>{formatCurrency(dashboardStats.pendingARAmount)}</div>
                      </div>
                      <div style={{ padding: '14px 16px', background: '#ede9fe', borderRadius: 8, borderLeft: '4px solid #8b5cf6' }}>
                        <div style={{ fontSize: 13, color: '#475569' }}>AR Released</div>
                        <div style={{ fontSize: 20, fontWeight: 700, color: '#8b5cf6' }}>{formatNumber(dashboardStats.releasedAR)}</div>
                        <div style={{ fontSize: 12, color: '#64748b' }}>{formatCurrency(dashboardStats.releasedARAmount)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="loading">Failed to load dashboard stats</div>
            )}
          </div>
        ) : (
          <>
            {/* Search */}
            <div className="table-filters-row">
              <div className="table-search-wrap" style={{ maxWidth: 360 }}>
                <Search size={15} className="table-search-icon" />
                <input
                  className="search-input table-search-input"
                  placeholder="Search invoice, customer..."
                  value={search}
                  onChange={e => { setSearch(e.target.value); clearSelection(); }}
                />
              </div>
              {activeTab === 'pending' && (
                <>
                  <div className="table-date-field">
                    <span>Date Delivered:</span>
                    <input
                      type="date"
                      className="filter-select"
                      value={pendingDateFrom}
                      onChange={e => { setPendingDateFrom(e.target.value); clearSelection(); }}
                      title="Deliveries dated on this date or later"
                    />
                    <span>to</span>
                    <input
                      type="date"
                      className="filter-select"
                      value={pendingDateTo}
                      onChange={e => { setPendingDateTo(e.target.value); clearSelection(); }}
                      title="Deliveries dated on this date or earlier"
                    />
                  </div>
                  {(pendingDateFrom || pendingDateTo) && (
                    <button
                      className="deliveries-filter-clear"
                      onClick={() => { setPendingDateFrom(''); setPendingDateTo(''); clearSelection(); }}
                      title="Clear the date filter"
                    >
                      <RotateCcw size={12} /> Clear
                    </button>
                  )}
                </>
              )}
              {activeTab === 'received' && (
                <>
                  <div className="table-date-field">
                    <span>Date Delivered:</span>
                    <input
                      type="date"
                      className="filter-select"
                      value={dateDeliveredFrom}
                      onChange={e => setDateDeliveredFrom(e.target.value)}
                      title="Deliveries dated on this date or later"
                    />
                    <span>to</span>
                    <input
                      type="date"
                      className="filter-select"
                      value={dateDeliveredTo}
                      onChange={e => setDateDeliveredTo(e.target.value)}
                      title="Deliveries dated on this date or earlier"
                    />
                  </div>
                  <div className="table-date-field">
                    <span>Picker:</span>
                    <select
                      className="filter-select"
                      value={picker}
                      onChange={e => setPicker(e.target.value)}
                      style={{ minWidth: 150 }}
                    >
                      <option value="">All Pickers</option>
                      {pickers.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                </>
              )}
              {activeTab === 'all' && (
                <div className="table-date-field">
                  <span>Status:</span>
                  <select
                    className="filter-select"
                    value={statusFilter}
                    onChange={e => setStatusFilter(e.target.value)}
                    style={{ minWidth: 140 }}
                  >
                    <option value="">All Status</option>
                    <option value="DELIVERED">Delivered</option>
                    <option value="PENDING">Pending</option>
                    <option value="RETURNED">Returned</option>
                    <option value="IN TRANSIT">In Transit</option>
                  </select>
                </div>
              )}
            </div>

            {/* Bulk action bar - appears once deliveries are ticked */}
            {activeTab === 'pending' && selectedCount > 0 && (
              <div className="bulk-bar">
                <div className="bulk-bar-info">
                  <CheckCircle size={16} />
                  <span>
                    <strong>{selectedCount}</strong> deliver{selectedCount === 1 ? 'y' : 'ies'} selected
                    <span style={{ color: '#94a3b8' }}> &nbsp;|&nbsp; </span>
                    Total <strong>{formatCurrency(selectedAmount)}</strong>
                  </span>
                </div>
                <div className="bulk-bar-actions">
                  <button className="btn btn-sm btn-secondary" onClick={clearSelection}>Clear Selection</button>
                  <button className="btn btn-sm" onClick={openBulkReceiveModal} style={{ background: '#10b981', color: 'white', border: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <CheckCircle size={14} /> Receive Selected
                  </button>
                </div>
              </div>
            )}

            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    {activeTab === 'pending' && (
                      <th style={{ width: 42, textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={allPageSelected}
                          ref={el => { if (el) el.indeterminate = somePageSelected; }}
                          onChange={toggleSelectAll}
                          title="Select all rows on this page"
                          style={{ cursor: 'pointer', width: 15, height: 15 }}
                        />
                      </th>
                    )}
                    <SortableTh column="date">Date</SortableTh>
                    <SortableTh column="date_delivered">Date Delivered</SortableTh>
                    <SortableTh column="invoice_number">Invoice #</SortableTh>
                    <SortableTh column="customer_code">Customer Code</SortableTh>
                    <SortableTh column="customer_name">Customer</SortableTh>
                    <SortableTh column="amount">Amount</SortableTh>
                    <SortableTh column="payment_terms">Payment Terms</SortableTh>
                    <SortableTh column="due_date">Due Date</SortableTh>
                    <SortableTh column="delivery_status">Status</SortableTh>
                    <SortableTh column="aging_days">Aging (Days)</SortableTh>
                    <SortableTh column="date_received_by_ar">Date Recv. by AR</SortableTh>
                    <th>AR Remarks</th>
                    <th>Color AR</th>
                    {activeTab !== 'pending' && <SortableTh column="ar_released_date">Posting Date</SortableTh>}
                    <SortableTh column="psi_posting_date">Document Date</SortableTh>
                    {activeTab !== 'all' && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={activeTab === 'all' ? 15 : 16} style={{ textAlign: 'center', padding: 40 }}>Loading...</td></tr>
                  ) : deliveries.length === 0 ? (
                    <tr><td colSpan={activeTab === 'all' ? 15 : 16} style={{ textAlign: 'center', padding: 40 }}>No records found</td></tr>
                  ) : deliveries.map((d, i) => {
                    const aging = calcAging(d.date_delivered);
                    const agingNum = typeof aging === 'number' ? aging : 0;
                    return (
                      <tr key={`${d.id}-${i}`}>
                        {activeTab === 'pending' && (
                          <td style={{ textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={!!selected[d.id]}
                              onChange={() => toggleRowSelection(d)}
                              disabled={!isSelectable(d)}
                              title={isSelectable(d) ? `Select ${d.invoice_number || 'delivery'}` : 'Not pending AR'}
                              style={{ cursor: isSelectable(d) ? 'pointer' : 'not-allowed', width: 15, height: 15 }}
                            />
                          </td>
                        )}
                        <td>{formatDate(d.date)}</td>
                        <td>{formatDate(d.date_delivered)}</td>
                        <td style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 600 }}>{d.invoice_number || '-'}</td>
                        <td style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 600 }}>{d.customer_code || '-'}</td>
                        <td>
                          <div style={{ fontWeight: 500 }}>{d.customer_name || '-'}</div>
                        </td>
                        <td className="amount">{formatCurrency(d.amount)}</td>
                        <td>
                          {d.payment_terms ? (
                            <span style={{ background: '#dbeafe', color: '#1e40af', padding: '2px 8px', borderRadius: 10, fontSize: 12, fontWeight: 600 }}>{d.payment_terms}</span>
                          ) : '-'}
                        </td>
                        <td>
                          {(() => {
                            const dueDate = calculateDueDate(d.date_delivered, d.payment_terms);
                            if (!dueDate) {
                              return <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>;
                            }
                            const overdue = isOverdue(dueDate);
                            return (
                              <span style={{ 
                                display: 'inline-flex', 
                                alignItems: 'center', 
                                gap: 6, 
                                padding: '2px 10px', 
                                borderRadius: 10, 
                                fontSize: 12, 
                                fontWeight: 600,
                                background: overdue ? '#fee2e2' : '#d1fae5',
                                color: overdue ? '#991b1b' : '#065f46'
                              }}>
                                <span style={{ 
                                  width: 8, 
                                  height: 8, 
                                  borderRadius: '50%', 
                                  background: overdue ? '#ef4444' : '#10b981' 
                                }}></span>
                                {formatDate(dueDate)}
                              </span>
                            );
                          })()}
                        </td>
                        <td>
                          <span style={{
                            background: d.delivery_status === 'DELIVERED' ? '#d1fae5' : d.delivery_status === 'PENDING' ? '#fef3c7' : '#e2e8f0',
                            color: d.delivery_status === 'DELIVERED' ? '#065f46' : d.delivery_status === 'PENDING' ? '#92400e' : '#475569',
                            padding: '2px 10px', borderRadius: 10, fontSize: 12, fontWeight: 600
                          }}>
                            {d.delivery_status || 'N/A'}
                          </span>
                        </td>
                        <td>
                          {typeof aging === 'number' ? (
                            <span style={{
                              background: `${agingColor(agingNum)}15`, color: agingColor(agingNum),
                              padding: '2px 10px', borderRadius: 10, fontSize: 13, fontWeight: 700
                            }}>
                              {aging}
                            </span>
                          ) : aging}
                        </td>
                        <td>
                          {d.date_received_by_ar ? (
                            <span>{formatDate(d.date_received_by_ar)}</span>
                          ) : (
                            <span style={{ color: '#94a3b8', fontSize: 12 }}>Not received</span>
                          )}
                        </td>
                        <td style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12 }}>
                          {d.ar_remarks || <span style={{ color: '#cbd5e1' }}>—</span>}
                        </td>
                        <td>
                          {d.color_ar ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: d.color_ar.toLowerCase() === 'green' ? '#d1fae5' : d.color_ar.toLowerCase() === 'yellow' ? '#fef3c7' : d.color_ar.toLowerCase() === 'red' ? '#fee2e2' : '#f1f5f9', color: d.color_ar.toLowerCase() === 'green' ? '#065f46' : d.color_ar.toLowerCase() === 'yellow' ? '#92400e' : d.color_ar.toLowerCase() === 'red' ? '#991b1b' : '#475569' }}>
                              <span style={{ width: 8, height: 8, borderRadius: '50%', background: d.color_ar.toLowerCase() === 'green' ? '#10b981' : d.color_ar.toLowerCase() === 'yellow' ? '#f59e0b' : d.color_ar.toLowerCase() === 'red' ? '#ef4444' : '#94a3b8' }}></span>
                              {d.color_ar}
                            </span>
                          ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                        </td>
                        {activeTab !== 'pending' && (
                          <td>{d.ar_released_date ? formatDate(d.ar_released_date) : d.psi_posting_date ? formatDate(d.psi_posting_date) : (
                            <span style={{ color: '#94a3b8', fontSize: 12 }}>Not released</span>
                          )}</td>
                        )}
                        <td>{d.psi_posting_date ? formatDate(d.psi_posting_date) : (
                          <span style={{ color: '#94a3b8', fontSize: 11 }}>No match</span>
                        )}</td>
                        {activeTab !== 'all' && (
                          <td>
                            <div style={{ display: 'flex', gap: 4 }}>
                              {activeTab === 'received' && d.date_received_by_ar && (
                                <button className="btn btn-sm" onClick={() => openEditDateModal(d)} title="Edit date received" style={{ background: '#64748b', color: 'white', border: 'none' }}>
                                  <Edit2 size={14} />
                                </button>
                              )}
                              {activeTab === 'pending' && !d.date_received_by_ar && (
                                <button className="btn btn-sm" onClick={() => openReceiveModal(d)} title="Receive by AR" style={{ background: '#10b981', color: 'white', border: 'none' }}>
                                  <CheckCircle size={14} />
                                </button>
                              )}
                              {activeTab === 'pending' && d.date_received_by_ar && !d.ar_released_date && !d.psi_posting_date && (
                                <button className="btn btn-sm" onClick={() => handleRelease(d)} title="Release to AR (date from document date)" style={{ background: '#3b82f6', color: 'white', border: 'none' }}>
                                  <Send size={14} />
                                </button>
                              )}
                              {activeTab === 'received' && !d.ar_released_date && !d.psi_posting_date && (
                                <button className="btn btn-sm" onClick={() => handleRelease(d)} title="Release to AR (date from document date)" style={{ background: '#3b82f6', color: 'white', border: 'none' }}>
                                  <Send size={14} />
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {activeTab !== 'dashboard' && (
        <div className="pagination">
          <div className="pagination-info">
            Page {pagination.page} of {pagination.totalPages} ({pagination.total.toLocaleString()} records)
          </div>
          <div className="pagination-buttons">
            <button className="page-btn" disabled={pagination.page <= 1} onClick={() => load(1)}>First</button>
            <button className="page-btn" disabled={pagination.page <= 1} onClick={() => load(pagination.page - 1)}><ChevronLeft size={16} /></button>
            <button className="page-btn" disabled={pagination.page >= pagination.totalPages} onClick={() => load(pagination.page + 1)}><ChevronRight size={16} /></button>
            <button className="page-btn" disabled={pagination.page >= pagination.totalPages} onClick={() => load(pagination.totalPages)}>Last</button>
          </div>
        </div>
      )}
    </div>
  );
}
